/* eslint-disable jsx-a11y/anchor-is-valid */
import { useState, useEffect } from 'react'
import * as Yup from 'yup'
import clsx from 'clsx'
import { Link } from 'react-router-dom'
import { useFormik } from 'formik'
import { login } from '../core/_requests'
import { useAuth } from '../core/Auth'
import { HandleApiErrors } from '../../../core/helpers/errorHandling'
import { useIntl } from 'react-intl'

const loginSchema = Yup.object().shape({
  email: Yup.string()
    .email('Wrong email format')
    .min(3, 'Minimum 3 symbols')
    .max(50, 'Maximum 50 symbols')
    .required('Email is required'),
  password: Yup.string()
    .min(3, 'Minimum 3 symbols')
    .max(50, 'Maximum 50 symbols')
    .required('Password is required'),
})

const initialValues = {
  email: '',
  password: '',
}

export function Login() {

  const [loading, setLoading] = useState(false)
  const { saveAuth, setCurrentUser } = useAuth()

  const intl = useIntl()
  const [errors, setErrors] = useState<Record<string, string>>();
  const [lockoutTime, setLockoutTime] = useState<number>(0);

  useEffect(() => {
    const checkStatus = () => {
      const lockoutUntil = localStorage.getItem('login_lockout_until');
      if (lockoutUntil) {
        const remaining = Math.ceil((parseInt(lockoutUntil) - Date.now()) / 1000);
        if (remaining > 0) {
          setLockoutTime(remaining);
        } else {
          setLockoutTime(0);
          localStorage.removeItem('login_lockout_until');
          localStorage.removeItem('login_failed_attempts');
        }
      }
    };

    checkStatus();
    const interval = setInterval(checkStatus, 1000);
    return () => clearInterval(interval);
  }, []);

  const handleErrors = (field: string, errorMessage: string) => {
    setErrors((prevErrors) => ({
      ...prevErrors,
      [field]: errorMessage,
    }));
  };

  const incrementFailedAttempts = () => {
    const attempts = parseInt(localStorage.getItem('login_failed_attempts') || '0') + 1;
    localStorage.setItem('login_failed_attempts', attempts.toString());
    if (attempts >= 5) {
      const lockoutMinutes = 5;
      const until = Date.now() + lockoutMinutes * 60 * 1000;
      localStorage.setItem('login_lockout_until', until.toString());
      setLockoutTime(lockoutMinutes * 60);
    }
  };

  const resetFailedAttempts = () => {
    localStorage.removeItem('login_failed_attempts');
    localStorage.removeItem('login_lockout_until');
    setLockoutTime(0);
  };

  const formik = useFormik({
    initialValues,
    validationSchema: loginSchema,
    onSubmit: async (values, { setStatus, setSubmitting }) => {
      if (lockoutTime > 0) {
        handleErrors('serverError', `Troppi tentativi falliti. Riprova tra ${lockoutTime} secondi.`);
        setSubmitting(false);
        return;
      }

      setLoading(true)
      try {
        const { data: auth } = await login(values.email, values.password)
        resetFailedAttempts()
        saveAuth(auth)
        //const { data: user } = await getUserByToken(auth.token)
        setCurrentUser(auth.user)
      } catch (error) {
        console.error(error)
        incrementFailedAttempts()
        handleErrors('serverError', HandleApiErrors(intl, error));
        saveAuth(undefined)
        setStatus('The login details are incorrect')
        setSubmitting(false)
        setLoading(false)
      }
    },
  })

  return (
    <form
      className='form w-100'
      onSubmit={formik.handleSubmit}
      noValidate
      id='kt_login_signin_form'
    >
      {/* begin::Heading */}
      <div className='text-center mb-11'>
        <h1 className='text-dark fw-bolder mb-3'>Accedi</h1>
        <div className='text-gray-500 fw-semibold fs-6'>Effettua l'accesso per utilizzare BrainMix</div>
      </div>
      {/* begin::Heading */}

      {/* begin::Form group */}
      <div className='fv-row mb-8'>
        <label className='form-label fs-6 fw-bolder text-dark'>Email</label>
        <input
          placeholder='Inserisci la tua email'
          {...formik.getFieldProps('email')}
          className={clsx(
            'form-control bg-transparent',
            { 'is-invalid': formik.touched.email && formik.errors.email },
            {
              'is-valid': formik.touched.email && !formik.errors.email,
            }
          )}
          type='email'
          name='email'
          autoComplete='on'
        />
        {formik.touched.email && formik.errors.email && (
          <div className='fv-plugins-message-container'>
            <span role='alert'>{formik.errors.email}</span>
          </div>
        )}
      </div>
      {/* end::Form group */}

      {/* begin::Form group */}
      <div className='fv-row mb-3'>
        <label className='form-label fw-bolder text-dark fs-6 mb-0'>Password</label>
        <input
          placeholder='Inserisci la tua password'
          type='password'
          autoComplete='on'
          {...formik.getFieldProps('password')}
          className={clsx(
            'form-control bg-transparent',
            {
              'is-invalid': formik.touched.password && formik.errors.password,
            },
            {
              'is-valid': formik.touched.password && !formik.errors.password,
            }
          )}
        />
        {formik.touched.password && formik.errors.password && (
          <div className='fv-plugins-message-container'>
            <div className='fv-help-block'>
              <span role='alert'>{formik.errors.password}</span>
            </div>
          </div>
        )}
      </div>
      {/* end::Form group */}

      {/* begin::Wrapper */}
      <div className='d-flex flex-stack flex-wrap gap-3 fs-base fw-semibold mb-8'>
        <div />

        {/* begin::Link */}
        <Link to='/auth/forgot-password' className='link-primary'>
          Password dimenticata?
        </Link>
        {/* end::Link */}
      </div>
      {/* end::Wrapper */}

      {/* begin::Action */}
      <div className='d-grid mb-10'>
        <button
          type='submit'
          id='kt_sign_in_submit'
          className='btn btn-primary'
          disabled={formik.isSubmitting || !formik.isValid || lockoutTime > 0}
        >
          {!loading && <span className='indicator-label'>Accedi</span>}
          {loading && (
            <span className='indicator-progress' style={{ display: 'block' }}>
              Accesso in corso...
              <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
            </span>
          )}
        </button>
        {lockoutTime > 0 && (
          <div className='fv-plugins-message-container'>
            <div className='fv-help-block text-danger fw-bold mt-2'>
              Account temporaneamente bloccato. Riprova tra {lockoutTime} secondi.
            </div>
          </div>
        )}
        {errors?.serverError && lockoutTime === 0 && (
          <div className='fv-plugins-message-container'>
            <div data-field='appname' data-validator='notEmpty' className='fv-help-block'>
              {errors?.serverError}
            </div>
          </div>
        )}
      </div>
      {/* end::Action */}

      <div className='text-gray-500 text-center fw-semibold fs-5'>
        Non sei ancora registrato?{' '}
        {/* <a href="https://brainmix.ai/attiva" className="link-primary">Registrati</a> */}
        <Link to='/auth/signup' className='link-primary'>
          Registrati
        </Link>
      </div>
    </form>
  )
}
